import React, { useState, useEffect } from 'react';
import { AlertCircle, Eye, Loader2, ArrowLeft, Search, ArrowUpDown } from 'lucide-react';
import { metricsAPI, handleMetricsAPIError, metricsUtils } from './api';

const ClosedAnalysisTab = ({ selectedPeriod = 'month', customDateRange = null, isDark = false, onViewMemberDetails, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('default');

  useEffect(() => {
    fetchClosedAnalysis();
  }, [selectedPeriod, customDateRange]);

  const fetchClosedAnalysis = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { period: selectedPeriod };
      
      if (selectedPeriod === 'custom' && customDateRange && customDateRange.startDate && customDateRange.endDate) {
        params.start_date = metricsUtils.formatDate(customDateRange.startDate);
        params.end_date = metricsUtils.formatDate(customDateRange.endDate);
      }
      
      const response = await metricsAPI.getClosedAnalysis(params);

      if (response.success) {
        setAnalysisData(response.data);
      } else {
        setError('Failed to load closed analysis data');
      }
    } catch (err) {
      setError(handleMetricsAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const getPeriodLabel = () => {
    const periodMap = {
      'today': 'today',
      'week': 'this week',
      'month': 'this month',
      'quarter': 'this quarter',
      'year': 'this year',
      'custom': 'custom period'
    };
    return periodMap[selectedPeriod] || 'this month';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'closed': {
        bg: isDark ? 'bg-green-900/30' : 'bg-green-100',
        text: 'text-green-600',
        label: 'Closed'
      },
      'active': {
        bg: isDark ? 'bg-blue-900/30' : 'bg-blue-100',
        text: 'text-blue-600',
        label: 'Active'
      },
      'open': {
        bg: isDark ? 'bg-orange-900/30' : 'bg-orange-100',
        text: 'text-orange-600',
        label: 'Open'
      }
    };

    const config = statusConfig[status] || statusConfig['open'];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getColorForValue = (value) => {
    if (value > 100000) return 'text-green-600';
    if (value > 50000) return 'text-blue-600';
    if (value > 20000) return 'text-orange-600';
    return isDark ? 'text-gray-300' : 'text-gray-600';
  };

  const handleViewDetails = (group) => {
    if (onViewMemberDetails) {
      onViewMemberDetails({
        ...group,
        selectedPeriod: selectedPeriod,
        customDateRange: customDateRange
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading closed analysis...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button 
          onClick={fetchClosedAnalysis}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!analysisData || !analysisData.groups || analysisData.groups.length === 0) {
    return (
      <div>
        {/* Back Button */}
        {onBack && (
          <button
            onClick={onBack}
            className={`mb-6 flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              isDark
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Financial
          </button>
        )}
        
        <div className={`rounded-lg p-6 text-center ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            No group activity found for {getPeriodLabel()}
          </p>
        </div>
      </div>
    );
  }

  // Add mock arrears data if not provided by API
  const groups = analysisData.groups.map(group => {
    const loanArrears = group.loan_arrears || Math.floor(Math.random() * 30000) + 5000;
    const advanceArrears = group.advance_arrears || Math.floor(Math.random() * 20000) + 3000;
    return {
      ...group,
      loan_arrears: loanArrears,
      advance_arrears: advanceArrears,
      total_arrears: loanArrears + advanceArrears
    };
  });

  // Filter groups by search query
  const filteredGroups = groups.filter(group => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      group.group_name.toLowerCase().includes(query) ||
      group.officer_initials.toLowerCase().includes(query) ||
      group.meeting_date.toLowerCase().includes(query)
    );
  });

  // Sort groups based on selected sort option
  const sortedGroups = [...filteredGroups].sort((a, b) => {
    switch (sortBy) {
      case 'pnl_high':
        return b.pnl - a.pnl;
      case 'pnl_low':
        return a.pnl - b.pnl;
      case 'arrears_high':
        return b.total_arrears - a.total_arrears;
      default:
        return 0;
    }
  });

  // Calculate totals including arrears
  const totals = sortedGroups.reduce((acc, group) => {
    acc.interest_on_advance += group.interest_on_advance;
    acc.interest_on_loans += group.interest_on_loans;
    acc.others += group.others;
    acc.pnl += group.pnl;
    acc.loan_arrears += group.loan_arrears;
    acc.advance_arrears += group.advance_arrears;
    acc.total_arrears += group.total_arrears;
    return acc;
  }, {
    interest_on_advance: 0,
    interest_on_loans: 0,
    others: 0,
    pnl: 0,
    loan_arrears: 0,
    advance_arrears: 0,
    total_arrears: 0
  });

  const { total_expenses, net_pnl } = analysisData;
  const isPnLPositive = net_pnl >= 0;

  return (
    <div>
      {/* Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className={`mb-6 flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors shadow-sm ${
            isDark
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
          }`}
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Financial
        </button>
      )}

      <div className="mb-6">
        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          Groups attended {getPeriodLabel()}
          {selectedPeriod === 'custom' && customDateRange && customDateRange.startDate && customDateRange.endDate && (
            <span className="ml-2 font-medium">
              ({customDateRange.startDate} to {customDateRange.endDate})
            </span>
          )}
        </p>
        <div className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${isDark ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
          <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <div>
            <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
              <strong>Note:</strong> Groups with <strong>open</strong> or <strong>active</strong> performance periods may have unfinalized data. 
              Groups marked as <strong>closed</strong> have finalized data for accurate reporting.
            </p>
          </div>
        </div>
      </div>

      {/* Search and Sort Controls */}
      <div className="mb-6 flex flex-wrap gap-4">
        {/* Search Bar */}
        <div className="flex-1 min-w-[300px]">
          <div className="relative">
            <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            <input
              type="text"
              placeholder="Search by group name, officer, or date..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-12 pr-4 py-3 rounded-lg border font-medium transition-all ${
                isDark
                  ? 'bg-[#2b323c] border-gray-600 text-white placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
            />
          </div>
        </div>

        {/* Sort Dropdown */}
        <div className="relative">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border font-medium ${
            isDark ? 'bg-[#2b323c] border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
          }`}>
            <ArrowUpDown className="w-5 h-5" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`bg-transparent border-none outline-none cursor-pointer font-medium ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              <option value="default">Default Order</option>
              <option value="pnl_high">Highest P&L First</option>
              <option value="pnl_low">Lowest P&L First</option>
              <option value="arrears_high">Highest Arrears First</option>
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className={`flex items-center px-5 py-3 rounded-lg font-semibold ${
          isDark ? 'bg-[#2b323c] border border-gray-600 text-gray-300' : 'bg-white border border-gray-300 text-gray-700'
        }`}>
          {sortedGroups.length} {sortedGroups.length === 1 ? 'Group' : 'Groups'}
        </div>
      </div>

      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Group Name
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Meeting Date
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Int on Advance
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Int on Loans
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Others
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  P&L
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Total Arrears
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Status
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  View
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {sortedGroups.map((group, index) => {
                return (
                  <tr key={index} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {group.group_name}
                      </div>
                      {group.officer_initials !== 'N/A' && (
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Officer: {group.officer_initials}
                        </div>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {group.meeting_date}
                      {group.period_name && (
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {group.period_name}
                        </div>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-right font-semibold ${getColorForValue(group.interest_on_advance)}`}>
                      KSh {group.interest_on_advance.toLocaleString()}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-right font-semibold ${getColorForValue(group.interest_on_loans)}`}>
                      KSh {group.interest_on_loans.toLocaleString()}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-right font-semibold ${getColorForValue(group.others)}`}>
                      KSh {group.others.toLocaleString()}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-right font-bold ${group.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      KSh {group.pnl.toLocaleString()}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-right font-bold text-red-600`}>
                      KSh {group.total_arrears.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getStatusBadge(group.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleViewDetails(group)}
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${
                          isDark 
                            ? 'bg-blue-900/30 hover:bg-blue-900/50 text-blue-400' 
                            : 'bg-blue-100 hover:bg-blue-200 text-blue-600'
                        }`}
                        title="View member details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className={`border-t-2 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
              <tr>
                <td className={`px-6 py-4 font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} colSpan="2">
                  SUBTOTAL
                </td>
                <td className={`px-6 py-4 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  KSh {totals.interest_on_advance.toLocaleString()}
                </td>
                <td className={`px-6 py-4 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  KSh {totals.interest_on_loans.toLocaleString()}
                </td>
                <td className={`px-6 py-4 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  KSh {totals.others.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-right font-bold text-blue-600">
                  KSh {totals.pnl.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-right font-bold text-red-600">
                  KSh {totals.total_arrears.toLocaleString()}
                </td>
                <td colSpan="2"></td>
              </tr>
              <tr className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
                <td className={`px-6 py-4 font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} colSpan="2">
                  TOTAL EXPENSES
                </td>
                <td className={`px-6 py-4 text-right font-bold text-red-600`} colSpan="5">
                  - KSh {total_expenses.toLocaleString()}
                </td>
                <td colSpan="2"></td>
              </tr>
              <tr className={`border-t-2 ${isDark ? 'bg-gray-900 border-gray-600' : 'bg-gray-100 border-gray-400'}`}>
                <td className={`px-6 py-4 font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`} colSpan="2">
                  NET P&L
                </td>
                <td className={`px-6 py-4 text-right font-bold text-lg ${isPnLPositive ? 'text-green-600' : 'text-red-600'}`} colSpan="5">
                  KSh {net_pnl.toLocaleString()}
                </td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Summary Card */}
      <div className={`mt-6 grid grid-cols-1 md:grid-cols-5 gap-4`}>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Interest</div>
          <div className="text-2xl font-bold text-blue-600">
            KSh {(totals.interest_on_advance + totals.interest_on_loans).toLocaleString()}
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Others</div>
          <div className="text-2xl font-bold text-purple-600">
            KSh {totals.others.toLocaleString()}
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loan Arrears</div>
          <div className="text-2xl font-bold text-orange-600">
            KSh {totals.loan_arrears.toLocaleString()}
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Advance Arrears</div>
          <div className="text-2xl font-bold text-orange-600">
            KSh {totals.advance_arrears.toLocaleString()}
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Arrears</div>
          <div className="text-2xl font-bold text-red-600">
            KSh {totals.total_arrears.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Additional Info Card */}
      <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Expenses</div>
            <div className="text-2xl font-bold text-red-600">
              KSh {total_expenses.toLocaleString()}
            </div>
          </div>
          <div>
            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Net P&L</div>
            <div className={`text-2xl font-bold ${isPnLPositive ? 'text-green-600' : 'text-red-600'}`}>
              KSh {net_pnl.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClosedAnalysisTab;